/**
 * healthcareImportRunner: the HEALTHCARE body, on the shared commit sequence.
 * ───────────────────────────────────────────────────────────────────────────
 * The sequencing, the section-id recovery, the upload-once-per-file cache, the
 * extra-angle rule, the run log and the inactive-by-default gate all live in
 * catalogueImportRunner.js, the same code the brand importer commits through.
 * What is left here is the one genuinely pharmacy-specific thing in a commit:
 * WHICH FIELDS the healthcare catalogue stores and what it calls them.
 *
 * TWO DIFFERENCES FROM THE BRAND BODY, both forced by the endpoint rather than
 * chosen:
 *
 *   1. `category` GOES AS OBJECTS, not ids. createHealthcareProduct does
 *      `category.map((cat) => cat._id)`, so a bare array of ids maps to a list
 *      of undefineds and the product saves with no category at all, silently.
 *      It also throws outright on an absent `category`, which is why an
 *      uncategorised product still sends an empty array.
 *   2. `sectionId` AND `healthcareId` RIDE IN THE BODY. The brand endpoint
 *      takes both in the path; the healthcare one is a flat POST to
 *      /healthcare/create-healthcare-product. The shared runner passes both to
 *      the body builder for exactly this reason.
 *
 * WHAT IS NEVER SENT, and why each omission is deliberate:
 *
 *   tierPrices          healthcare has no tier path and adding one silently
 *                       re-prices every wholesale line at the retail base.
 *                       See healthcareImportColumns.js.
 *   orderFlowOverride   a brand field; the healthcare schema has no such path.
 *   specs               the healthcare catalogue has no `specs` field.
 *   commissionRate, commissionAmount, vendorNetPrice, commissionCalculatedAt,
 *   commissionRateSource, country                       all server-derived.
 *
 * createHealthcareProduct whitelists its body field by field and DROPS
 * anything it does not destructure, so a mis-named key here fails by doing
 * nothing rather than by erroring. That is why the tests assert on the exact
 * payload.
 */
import { runCatalogueImport } from "./catalogueImportRunner.js";

/**
 * The healthcare product create body.
 *
 * `enabled` is handed in already resolved by the shared runner, which applies
 * the inactive-by-default policy (founder policy, 16 Sep 2026) for every
 * vertical at once. Do not re-derive it here, and do not add a way round it.
 */
export const buildHealthcareProductBody = ({
  item,
  image,
  additionalImages,
  enabled,
  sectionId,
  vendorId,
}) => {
  const body = {
    name: item.name,
    description: item.description || "",
    sectionId,
    healthcareId: vendorId,
    /* Founder rule, 29 Sep 2026: no item may sell at 0. A quoted item carries
       price 0 WITH `priceOnRequest` set, never a bare 0, and the validator has
       already refused any row that had a zero price without the posture. Same
       expression the brand body uses, for the same reason. */
    price: item.priceOnRequest ? 0 : Math.round(item.price || 0),
    productType: item.productType || "PRODUCT",
    requiresPrescription: !!item.requiresPrescription,
    ageRestriction: item.ageRestriction || 0,
    controlledSubstance: !!item.controlledSubstance,
    /* Null rather than "" when the product is not scheduled: the schema's own
       default is null, and an empty string is a value that would satisfy a
       truthiness check somewhere downstream and read as "scheduled, blank". */
    controlledSchedule: item.controlledSchedule || null,
    batches: item.batches || [],
    image,
    additionalImages,
    enabled,
    measurements:
      item.measurements === null || item.measurements === undefined
        ? 1
        : item.measurements,
    tags: item.tags,
    /* Objects, not ids. See the note at the top of this file. */
    category: (item.categoryIds || []).map((id) => ({ _id: id })),
    requiredPaymentMethod: item.requiredPaymentMethod || "NONE",
    /* The commercial layer a pharmacy shares with a brand (27 Sep 2026). */
    priceOnRequest: !!item.priceOnRequest,
    minOrderQty: item.minOrderQty === null ? 1 : Math.round(item.minOrderQty),
  };

  /* Stock lives in one field or the other, never both, because the schema
     makes each conditionally required on `productType`. Sending the one that
     does not apply would store a number nothing reads and that the vendor
     would later believe. */
  if (item.productType === "SERVICE") {
    body.availableSlots =
      item.availableSlots === null || item.availableSlots === undefined
        ? 10
        : Math.max(0, Math.round(item.availableSlots));
  } else {
    body.countInStock =
      item.countInStock === null || item.countInStock === undefined
        ? 1
        : Math.max(1, Math.round(item.countInStock));
  }

  /* Optional fields are OMITTED rather than sent empty, the house style on
     every Lusuku create form, and it keeps "" out of trimmed string paths.
     `vatCategory` in particular: absent means the schema default STANDARD, and
     sending "" would fail the enum. */
  if (item.vatCategory) body.vatCategory = item.vatCategory;
  if (item.skuCode) body.skuCode = item.skuCode;
  // Recorded on the product, so a placeholder cover stays findable long after
  // whoever ran the import has forgotten which rows had one.
  if (item.imageSource) body.imageSource = item.imageSource;
  if (item.unitId) body.unit_id = item.unitId;

  return body;
};

/**
 * @param {object}   plan                 output of healthcareImport.buildImportPlan
 * @param {object}   deps
 * @param {object}   deps.vendor          { _id, name } the healthcare vendor
 * @param {Function} deps.createSection   (vendorId, body) => res
 * @param {Function} deps.fetchSections   (vendorId) => res
 * @param {Function} deps.createProduct   (vendorId, sectionId, body) => res
 * @param {Function} deps.uploadImage     (file, opts) => { secure_url }
 * @param {boolean}  [deps.forceDisabled] import everything as enabled:false
 * @param {Function} [deps.onProgress]    ({ phase, done, total, label })
 * @returns {Promise<{created, failed, log}>}
 */
export const runHealthcareImport = async (plan, deps) =>
  runCatalogueImport(plan, {
    ...deps,
    buildBody: buildHealthcareProductBody,
    vendorMedia: {
      vendorType: "healthcare",
      entityType: "healthcareProduct",
      source: "dashboard-healthcare-import",
    },
  });

export default runHealthcareImport;
